'use client';

import { useMemo } from 'react';
import ConditionLegend from '@alltrails/conditions/components/ConditionLegend';
import useConditionPlayback from '@alltrails/conditions/hooks/useConditionPlayback';
import useLogMapOverlayTimeDrawerActionTapped from '@alltrails/conditions/hooks/useLogMapOverlayTimeDrawerActionTapped';
import { getLocalizedDateStart, getUTCFromLocalized } from '@alltrails/conditions/utils/timeHelpers';
import HeatmapLegend from '@alltrails/heatmaps/components/HeatmapLegend';
import { exploreConditionsIntervalMin } from '@/hooks/useExploreConditions';
import { useAppSelector } from '@/utils/redux';
import styles from './ExploreMap.module.scss';

const MapLegends = () => {
  const { activeCondition, selectedUTC, dynamicStyleIds } = useAppSelector(state => ({
    activeCondition: state.conditions.activeCondition,
    selectedUTC: state.conditions.selectedUTC,
    dynamicStyleIds: state.map.dynamicStyleIds
  }));

  const heatmapIsApplied = useMemo(() => dynamicStyleIds.includes('newHeatmap'), [dynamicStyleIds]);
  const logDateChangedEvent = useLogMapOverlayTimeDrawerActionTapped('date changed', 'explore');
  const logTimeScrubbedEvent = useLogMapOverlayTimeDrawerActionTapped('time scrubbed', 'explore');
  const logTimePlayedEvent = useLogMapOverlayTimeDrawerActionTapped('time played', 'explore');
  const logTimePausedEvent = useLogMapOverlayTimeDrawerActionTapped('time paused', 'explore');

  // The return of getTimezoneOffset is in minutes and positive values represent times behind UTC,
  // but we want seconds and negative values for behind UTC
  const utcOffsetSeconds = useMemo(() => new Date().getTimezoneOffset() * -60, []);
  const dayStartUTC = selectedUTC
    ? // Convert the selected utc -> local, get the local day start, convert the local day start -> utc day start
      getUTCFromLocalized(getLocalizedDateStart(selectedUTC, utcOffsetSeconds), utcOffsetSeconds)
    : undefined;
  useConditionPlayback(exploreConditionsIntervalMin, dayStartUTC);

  return (
    <div className={styles.mapLegendContainer}>
      {/* The conditions overlays and heatmap overlays are mutually exclusive, so we only show one at a time to avoid jarring transitions */}
      {activeCondition && activeCondition !== 'groundConditions' ? (
        <ConditionLegend
          activeCondition={activeCondition}
          dayAndTimeControls={{
            days: Array(7).fill({}),
            intervalMinutes: exploreConditionsIntervalMin,
            onDayChange: logDateChangedEvent,
            onPause: logTimePausedEvent,
            onPlay: logTimePlayedEvent,
            onTimeChange: logTimeScrubbedEvent,
            utcOffsetSeconds
          }}
        />
      ) : (
        <HeatmapLegend isOpen={heatmapIsApplied} />
      )}
    </div>
  );
};

export default MapLegends;
